import type {ChangeEvent} from 'react';
import {downloadBlobAsFile} from '@Utils/file.util';
import type {
  AgentOverrideRow,
  ForcedImageProvider,
  ForcedProviderOption,
  ForcedTextExtractionModel,
  ForcedTextExtractionModelSection,
  ForcedTextRemovalMethod,
  ForcedTextRemovalMethodSection,
  MockCredits,
  MockCreditsOption,
  OverrideSelectHandlerOptions,
  OverrideOptionsPageProp,
  OverrideSelectOption,
  OverrideSelectSection,
} from './ai-chat-super-user-options.types';
import {NO_OVERRIDE_VALUE, OVERRIDE_OPTIONS_PAGE_PROPS} from './ai-chat-super-user-options.types';

const BASE = 'ai';

const FORCED_IMAGE_PROVIDERS: readonly ForcedImageProvider[] = [NO_OVERRIDE_VALUE, 'gemini-3.1-flash-image-preview', 'openai-gpt-image', 'openai-gpt-image-sunburst', 'seedream', 'deepinfra-seedream-4-5', 'fal-grok-imagine-2'];

const MOCK_CREDIT_LEVELS: readonly MockCredits[] = [NO_OVERRIDE_VALUE, '0', '1', '2', '5', '20', '100'];

const MOCK_CREDIT_LABELS: Record<Exclude<MockCredits, typeof NO_OVERRIDE_VALUE>, string> = {
  '0': '0 credits',
  '1': '1 credit',
  '2': '2 credits',
  '5': '5 credits',
  '20': '20 credits',
  '100': '100 credits',
};

/** Must read the same as `TextExtractionModel::getLabel()`; the effort is half the choice, so it is in the label. */
export const IMAGE_PROVIDER_LABELS: Record<Exclude<ForcedImageProvider, typeof NO_OVERRIDE_VALUE>, string> = {
  'gemini-3.1-flash-image-preview': 'Nano Banana 2',
  'openai-gpt-image': 'GPT Image 2.5 Flare',
  'openai-gpt-image-sunburst': 'GPT Image 2.5 Sunburst',
  seedream: 'Seedream 5.0 Lite',
  'deepinfra-seedream-4-5': 'Seedream 4.5 (DeepInfra)',
  'fal-grok-imagine-2': 'Grok Imagine 2.0 (fal)',
};

// The retired preview id only ever appears on chat messages generated before the GA cutover.
const GEMINI_MODEL_VERSION_LABELS: Record<string, string> = {
  'gemini-3.1-flash-image': 'Nano Banana 2',
  'gemini-3.1-flash-image-preview': 'Nano Banana 2 (preview)',
};

export function getImageProviderLabel(provider: string, model?: string | null): string {
  if (model && model in GEMINI_MODEL_VERSION_LABELS) {
    return GEMINI_MODEL_VERSION_LABELS[model];
  }
  if (!isForcedImageProvider(provider) || provider === NO_OVERRIDE_VALUE) {
    return provider;
  }
  return IMAGE_PROVIDER_LABELS[provider];
}

export function getInitialAgents(): AgentOverrideRow[] {
  const raw: unknown = window.reactPageProps?.aiChatSuperUserVariantOverrides;
  return Array.isArray(raw) ? (raw as AgentOverrideRow[]) : [];
}

export function setSuperUserVariantOverride(agentType: string, alias: string): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserVariantOverride', {agentType, alias}) as Promise<void>;
}

export function clearSuperUserVariantOverrides(): Promise<void> {
  return window.PMW.writeLocal(BASE + '/clearSuperUserVariantOverrides', {}) as Promise<void>;
}

export async function downloadUserAttachments(chatId: string): Promise<void> {
  const params = new URLSearchParams({chatId});
  const url = `${window.PMW.util.site_url(BASE + '/superUserDownloadAllChatAttachments')}?${params.toString()}`;

  const res = await fetch(url, {credentials: 'same-origin'});
  if (!res.ok || isJsonResponse(res)) {
    throw await readSuperUserDownloadError(res);
  }

  const blob = await res.blob();
  downloadBlobAsFile(blob, `aichat-attachments-${chatId}.zip`);
}

function isJsonResponse(res: Response): boolean {
  const contentType = res.headers.get('Content-Type') ?? '';
  return contentType.toLowerCase().includes('application/json');
}

async function readSuperUserDownloadError(res: Response): Promise<Error> {
  const fallback = new Error(`Download failed: ${res.status}`);
  try {
    const body = (await res.json()) as {message?: string; status?: string};
    return body?.message ? new Error(body.message) : fallback;
  } catch {
    return fallback;
  }
}

/** Page props are attacker-shaped `unknown`, and a stored override the union no longer contains must degrade to "no override" rather than select a value the backend would reject. */
function readForcedOptionFromPageProps<TValue extends string>(rawValue: unknown, allowedValues: readonly TValue[], noOverrideValue: TValue): TValue {
  if (typeof rawValue !== 'string') {
    return noOverrideValue;
  }
  return allowedValues.find((allowedValue) => allowedValue === rawValue) ?? noOverrideValue;
}

function isOverrideSelectOption(value: unknown): value is OverrideSelectOption {
  return typeof value === 'object' && value !== null && typeof (value as OverrideSelectOption).value === 'string' && typeof (value as OverrideSelectOption).label === 'string';
}

function isRawOverrideSection(value: unknown): value is {heading: string | null; options: unknown[]} {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const {heading, options} = value as {heading?: unknown; options?: unknown};
  return (heading === null || typeof heading === 'string') && Array.isArray(options);
}

/** A prop that is missing or malformed yields no sections, so the dropdown falls back to "auto" rather than a stale list. */
function serverOverrideSections(pagePropName: OverrideOptionsPageProp): OverrideSelectSection[] {
  const raw: unknown = window.reactPageProps?.[pagePropName];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(isRawOverrideSection).map((section) => ({
    heading: section.heading,
    options: section.options.filter(isOverrideSelectOption),
  }));
}

/** `offeredValues()`/`isForced*()` MUST use this, not just the first section, or a stored override from another section is silently rejected. */
function flattenSectionOptions(sections: OverrideSelectSection[]): OverrideSelectOption[] {
  return sections.flatMap((section) => section.options);
}

function offeredValues(pagePropName: OverrideOptionsPageProp): string[] {
  return [NO_OVERRIDE_VALUE, ...flattenSectionOptions(serverOverrideSections(pagePropName)).map((option) => option.value)];
}

function isOfferedOverrideValue(value: string, options: OverrideSelectOption[]): boolean {
  return value === NO_OVERRIDE_VALUE || options.some((option) => option.value === value);
}

/** The "auto" entry is prepended as its own headingless section: its label is a translated string, not sent from the backend. */
function withNoOverrideSection(sections: OverrideSelectSection[]): OverrideSelectSection[] {
  const noOverrideSection: OverrideSelectSection = {
    heading: null,
    options: [{value: NO_OVERRIDE_VALUE, label: window.i18next.t('pmwjs_ai_chat_provider_auto')}],
  };
  return [noOverrideSection, ...sections];
}

/** Wraps a flat option list as the single headingless section the shared select renderer expects. */
export function asUngroupedSection(options: OverrideSelectOption[]): OverrideSelectSection[] {
  return [{heading: null, options}];
}

/** Every label map deliberately excludes `NO_OVERRIDE_VALUE` (its `Record<Exclude<…>>` type makes the rest exhaustive), so the unlabelled value is always the no-override entry and gets the translated "auto" label. */
function buildOverrideOptions<TValue extends string>(values: readonly TValue[], labelsByValue: Partial<Record<TValue, string>>): OverrideSelectOption<TValue>[] {
  return values.map((value) => ({
    value,
    label: labelsByValue[value] ?? window.i18next.t('pmwjs_ai_chat_provider_auto'),
  }));
}

/** Builds the `<select>` onChange for one forced-override row: ignore values outside the union, then persist before mirroring into local state so a failed save never shows as applied. */
export function createOverrideSelectHandler<TValue extends string>(options: OverrideSelectHandlerOptions<TValue>): (event: ChangeEvent<HTMLSelectElement>) => void {
  return (event: ChangeEvent<HTMLSelectElement>): void => {
    const value = event.target.value;
    if (!options.isValidValue(value)) {
      return;
    }
    void options.save(value).then(
      () => {
        options.onSaved(value);
      },
      () => {
        options.onError();
      }
    );
  };
}

export function isForcedImageProvider(value: string): value is ForcedImageProvider {
  return FORCED_IMAGE_PROVIDERS.some((provider) => provider === value);
}

export function getInitialForcedImageProvider(): ForcedImageProvider {
  return readForcedOptionFromPageProps(window.reactPageProps?.aiChatForcedImageProvider, FORCED_IMAGE_PROVIDERS, NO_OVERRIDE_VALUE);
}

export function setSuperUserForcedImageProvider(provider: ForcedImageProvider): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserForcedImageProvider', {provider}) as Promise<void>;
}

export function getForcedImageProviderOptions(): ForcedProviderOption[] {
  return buildOverrideOptions(FORCED_IMAGE_PROVIDERS, IMAGE_PROVIDER_LABELS);
}

export function isForcedTextRemovalMethod(value: string): value is ForcedTextRemovalMethod {
  return isOfferedOverrideValue(value, flattenSectionOptions(serverOverrideSections(OVERRIDE_OPTIONS_PAGE_PROPS.textRemovalMethod)));
}

export function getInitialForcedTextRemovalMethod(): ForcedTextRemovalMethod {
  return readForcedOptionFromPageProps(window.reactPageProps?.mteForcedTextRemovalMethod, offeredValues(OVERRIDE_OPTIONS_PAGE_PROPS.textRemovalMethod), NO_OVERRIDE_VALUE);
}

export function setSuperUserForcedTextRemovalMethod(method: ForcedTextRemovalMethod): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserForcedTextRemovalMethod', {method}) as Promise<void>;
}

export function getForcedTextRemovalMethodSections(): ForcedTextRemovalMethodSection[] {
  return withNoOverrideSection(serverOverrideSections(OVERRIDE_OPTIONS_PAGE_PROPS.textRemovalMethod));
}

export function isMockCredits(value: string): value is MockCredits {
  return MOCK_CREDIT_LEVELS.some((level) => level === value);
}

export function getInitialMockCredits(): MockCredits {
  return readForcedOptionFromPageProps(window.reactPageProps?.aiChatMockCredits, MOCK_CREDIT_LEVELS, NO_OVERRIDE_VALUE);
}

export function setSuperUserMockCredits(credits: MockCredits): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserMockCredits', {credits}) as Promise<void>;
}

export function getMockCreditsOptions(): MockCreditsOption[] {
  return buildOverrideOptions(MOCK_CREDIT_LEVELS, MOCK_CREDIT_LABELS);
}

export function isForcedTextExtractionModel(value: string): value is ForcedTextExtractionModel {
  return isOfferedOverrideValue(value, flattenSectionOptions(serverOverrideSections(OVERRIDE_OPTIONS_PAGE_PROPS.textExtractionModel)));
}

export function getInitialForcedTextExtractionModel(): ForcedTextExtractionModel {
  return readForcedOptionFromPageProps(window.reactPageProps?.mteForcedTextExtractionModel, offeredValues(OVERRIDE_OPTIONS_PAGE_PROPS.textExtractionModel), NO_OVERRIDE_VALUE);
}

export function setSuperUserForcedTextExtractionModel(model: ForcedTextExtractionModel): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserForcedTextExtractionModel', {model}) as Promise<void>;
}

export function getForcedTextExtractionModelSections(): ForcedTextExtractionModelSection[] {
  return withNoOverrideSection(serverOverrideSections(OVERRIDE_OPTIONS_PAGE_PROPS.textExtractionModel));
}
